/**
 * PreSales Sandbox test entity definitions.
 * Each entity maps to FrankieOne testbed data and produces specific
 * verification outcomes (CLEAR, PEP, SANCTION, FRAUD, etc.).
 */

export type EntityType = 'individual' | 'organization'

export interface SandboxEntity {
  id: string
  label: string
  description: string
  expectedResult: string
  documents: string[]
  flags: string[]
  type: EntityType
  workflow?: string
  individual?: Record<string, unknown>
  organization?: Record<string, unknown>
}

const CONSENTS = [
  { type: 'GENERAL' },
  { type: 'DOCS' },
  { type: 'CREDITHEADER' },
  { type: 'UNDER18' },
]

export const SANDBOX_ENTITIES: SandboxEntity[] = [
  {
    id: 'james-testone',
    label: 'James A Testone',
    description: 'Standard verification — passes all checks cleanly. Good baseline demo entity.',
    expectedResult: 'CLEAR',
    documents: ['Drivers Licence (VIC)', 'Medicare Card (Green)'],
    flags: [],
    type: 'individual',
    individual: {
      name: { givenName: 'JAMES', middleName: 'A', familyName: 'TESTONE' },
      dateOfBirth: { year: '1950', month: '01', day: '01' },
      addresses: [{
        type: 'RESIDENTIAL',
        status: 'CURRENT',
        unitNumber: 'U 1',
        streetNumber: '35',
        streetName: 'CONN',
        streetType: 'STREET',
        locality: 'FERNTREE GULLY',
        subdivision: 'VIC',
        postalCode: '3156',
        country: 'AUS',
      }],
      documents: {
        IDENTITY: [
          {
            type: 'DRIVERS_LICENSE',
            class: 'IDENTITY',
            primaryIdentifier: '283229690',
            secondaryIdentifier: 'P5403241',
            country: 'AUS',
            subdivision: 'VIC',
          },
          {
            type: 'NATIONAL_HEALTH_ID',
            class: 'IDENTITY',
            primaryIdentifier: '6603984391',
            subtype: 'G',
            country: 'AUS',
            expiryDate: { year: '2030', month: '01', day: '01', unstructured: '2030-01-01', type: 'GREGORIAN' },
            supplementaryData: {
              type: 'NATIONAL_HEALTH_ID',
              reference: '1',
              nameOnCardLine1: 'JAMES A TESTONE',
            },
          },
        ],
      },
      consents: CONSENTS,
    },
  },
  {
    id: 'judy-testtwelve',
    label: 'Judy H Testtwelve',
    description: 'Triggers a PEP (Politically Exposed Person) match. Verified with Medicare Card and Passport.',
    expectedResult: 'PEP',
    documents: ['Passport (AUS)', 'Medicare Card (Green)'],
    flags: ['PEP'],
    type: 'individual',
    individual: {
      name: { givenName: 'JUDY', middleName: 'H', familyName: 'TESTTWELVE' },
      dateOfBirth: { year: '1961', month: '01', day: '01' },
      addresses: [{
        type: 'RESIDENTIAL',
        streetNumber: '20',
        streetName: 'CARBINE',
        streetType: 'STREET',
        locality: 'WOORABINDA',
        subdivision: 'QLD',
        postalCode: '4713',
        country: 'AUS',
      }],
      documents: {
        IDENTITY: [
          {
            type: 'PASSPORT',
            class: 'IDENTITY',
            primaryIdentifier: 'E2747770',
            country: 'AUS',
          },
          {
            type: 'NATIONAL_HEALTH_ID',
            class: 'IDENTITY',
            primaryIdentifier: '6500006941',
            subtype: 'G',
            country: 'AUS',
            expiryDate: { year: '2030', month: '01', day: '01', unstructured: '2030-01-01', type: 'GREGORIAN' },
            supplementaryData: {
              type: 'NATIONAL_HEALTH_ID',
              reference: '1',
              nameOnCardLine1: 'JUDY H TESTTWELVE',
            },
          },
        ],
      },
      consents: CONSENTS,
    },
  },
  {
    id: 'bruce-testnineteen',
    label: 'Bruce Testnineteen',
    description: 'Triggers a Sanctions list match. Verified with an Australian Passport.',
    expectedResult: 'SANCTION',
    documents: ['Passport (AUS)'],
    flags: ['Sanction'],
    type: 'individual',
    individual: {
      name: { givenName: 'BRUCE', familyName: 'TESTNINETEEN' },
      dateOfBirth: { year: '1968', month: '01', day: '01' },
      addresses: [{
        type: 'RESIDENTIAL',
        streetNumber: '12',
        streetName: 'THROSSELL',
        streetType: 'STREET',
        locality: 'CURTIN',
        subdivision: 'ACT',
        postalCode: '2605',
        country: 'AUS',
      }],
      documents: {
        IDENTITY: [{
          type: 'PASSPORT',
          class: 'IDENTITY',
          primaryIdentifier: 'P3251638',
          country: 'AUS',
        }],
      },
      consents: CONSENTS,
    },
  },
  {
    id: 'mary-testtwentyfour',
    label: 'Mary O Testtwentyfour',
    description: 'Triple threat — triggers PEP, Sanction, and Fraud flags simultaneously.',
    expectedResult: 'PEP,SANCTION,FRAUD',
    documents: ['Drivers Licence (TAS)'],
    flags: ['PEP', 'Sanction', 'Fraud'],
    type: 'individual',
    individual: {
      name: { givenName: 'MARY', middleName: 'O', familyName: 'TESTTWENTYFOUR' },
      dateOfBirth: { year: '1973', month: '01', day: '01' },
      addresses: [{
        type: 'RESIDENTIAL',
        streetNumber: '10',
        streetName: 'TEMMA',
        streetType: 'ROAD',
        locality: 'ARTHUR RIVER',
        subdivision: 'TAS',
        postalCode: '7330',
        country: 'AUS',
      }],
      documents: {
        IDENTITY: [{
          type: 'DRIVERS_LICENSE',
          class: 'IDENTITY',
          primaryIdentifier: 'T5425427',
          secondaryIdentifier: 'T89188006',
          country: 'AUS',
          subdivision: 'TAS',
        }],
      },
      consents: CONSENTS,
    },
  },
  {
    id: 'stacy-testthirty',
    label: 'Stacy S Testthirty',
    description: 'Fraud warning flag. Verified with Drivers Licence and Birth Certificate.',
    expectedResult: 'FRAUD',
    documents: ['Drivers Licence (WA)', 'Birth Certificate (WA)'],
    flags: ['Fraud'],
    type: 'individual',
    individual: {
      name: { givenName: 'STACY', middleName: 'S', familyName: 'TESTTHIRTY' },
      dateOfBirth: { year: '1979', month: '01', day: '01' },
      addresses: [{
        type: 'RESIDENTIAL',
        streetNumber: '11',
        streetName: 'STATION',
        streetType: 'STREET',
        locality: 'YARLOOP',
        subdivision: 'WA',
        postalCode: '6218',
        country: 'AUS',
      }],
      documents: {
        IDENTITY: [
          {
            type: 'DRIVERS_LICENSE',
            class: 'IDENTITY',
            primaryIdentifier: '6035574',
            secondaryIdentifier: '3404118180',
            country: 'AUS',
            subdivision: 'WA',
          },
          {
            type: 'BIRTH_CERT',
            class: 'IDENTITY',
            primaryIdentifier: '146612361',
            secondaryIdentifier: '87654321',
            country: 'AUS',
            subdivision: 'WA',
            supplementaryData: {
              type: 'BIRTH_CERT',
              registrationDate: '1972-01-19',
              registrationDistrict: 'PERTH',
              dateOfPrint: '1999-12-31',
              registeredGivenName: 'STACY',
              registeredOtherNames: 'S',
              registeredFamilyName: 'TEST30',
            },
          },
        ],
      },
      consents: CONSENTS,
    },
  },
  {
    id: 'frank-testthirtyfive',
    label: 'Frank Testthirtyfive',
    description: 'Visa verification scenario — foreign passport (GBR) for VEVO check, plus Medicare. Triggers Fraud flag.',
    expectedResult: 'FRAUD',
    documents: ['Passport (GBR — Visa/VEVO)', 'Medicare Card (Yellow)'],
    flags: ['Fraud'],
    type: 'individual',
    individual: {
      name: { givenName: 'FRANK', familyName: 'TESTTHIRTYFIVE' },
      dateOfBirth: { year: '1984', month: '01', day: '01' },
      addresses: [{
        type: 'RESIDENTIAL',
        streetNumber: '273',
        streetName: 'JERRABOMBERRA',
        streetType: 'AVENUE',
        locality: 'SYMONSTON',
        subdivision: 'ACT',
        postalCode: '2609',
        country: 'AUS',
      }],
      documents: {
        IDENTITY: [
          {
            type: 'PASSPORT',
            class: 'IDENTITY',
            primaryIdentifier: 'E07148293',
            country: 'GBR',
          },
          {
            type: 'NATIONAL_HEALTH_ID',
            class: 'IDENTITY',
            primaryIdentifier: '5156598231',
            subtype: 'Y',
            country: 'AUS',
            expiryDate: { year: '2030', month: '01', day: '01', unstructured: '2030-01-01', type: 'GREGORIAN' },
            supplementaryData: {
              type: 'NATIONAL_HEALTH_ID',
              reference: '2',
              nameOnCardLine1: 'FRANK TESTTHIRTYFIVE',
            },
          },
        ],
      },
      consents: CONSENTS,
    },
  },
  {
    id: 'diane-nicholson',
    label: 'Diane Nicholson',
    description: 'Fake ID scenario — triggers PEP and Sanctions flags with a suspicious drivers licence.',
    expectedResult: 'PEP,SANCTION',
    documents: ['Drivers Licence (VIC — fake ID)'],
    flags: ['PEP', 'Sanction'],
    type: 'individual',
    individual: {
      name: { givenName: 'DIANE', familyName: 'NICHOLSON' },
      dateOfBirth: { year: '1990', month: '01', day: '01' },
      addresses: [{
        type: 'RESIDENTIAL',
        streetNumber: '710',
        streetName: 'Collins',
        streetType: 'Street',
        locality: 'Docklands',
        subdivision: 'VIC',
        postalCode: '3008',
        country: 'AUS',
      }],
      documents: {
        IDENTITY: [{
          type: 'DRIVERS_LICENSE',
          class: 'IDENTITY',
          primaryIdentifier: '11111111',
          secondaryIdentifier: 'P8257670',
          country: 'AUS',
          subdivision: 'VIC',
        }],
      },
      consents: CONSENTS,
    },
  },
  {
    id: 'hillary-clinton',
    label: 'Hillary Clinton',
    description: 'Real-world PEP match — US passport with visa. Demonstrates AML screening on public figures.',
    expectedResult: 'PEP',
    documents: ['Passport (USA — Visa/VEVO)'],
    flags: ['PEP'],
    type: 'individual',
    individual: {
      name: { givenName: 'HILLARY', familyName: 'CLINTON' },
      dateOfBirth: { year: '1947', month: '10', day: '26' },
      addresses: [{
        type: 'RESIDENTIAL',
        streetNumber: '710',
        streetName: 'Collins',
        streetType: 'Street',
        locality: 'Docklands',
        subdivision: 'VIC',
        postalCode: '3008',
        country: 'AUS',
      }],
      documents: {
        IDENTITY: [{
          type: 'PASSPORT',
          class: 'IDENTITY',
          primaryIdentifier: '22222222',
          country: 'USA',
          expiryDate: { year: '2030', month: '01', day: '01', unstructured: '2030-01-01', type: 'GREGORIAN' },
        }],
      },
      consents: CONSENTS,
    },
  },
  {
    id: 'driver-cover-source',
    label: 'Driver Cover (Duplicate Source)',
    description: 'Duplicate check — source entity created first as the anchor. Thai passport, NSW address.',
    expectedResult: 'CLEAR',
    documents: ['Passport (THA)'],
    flags: [],
    type: 'individual',
    individual: {
      name: { displayName: 'Driver Cover', givenName: 'Driver', familyName: 'Cover', language: 'ENG' },
      dateOfBirth: { year: '1985', month: '12', day: '15' },
      nationality: 'AUS', schemaVersion: 2,
      addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', streetNumber: '37', streetName: 'Koloona', streetType: 'Drive', locality: 'Tapitallee', subdivision: 'NSW', postalCode: '2540', country: 'AUS' }],
      documents: { IDENTITY: [{ type: 'PASSPORT', class: 'IDENTITY', primaryIdentifier: 'TH8598742', country: 'THA' }] },
      consents: CONSENTS,
    },
  },
  {
    id: 'driver-cover-duplicate',
    label: 'Driver Cover (Duplicate Trigger)',
    description: 'Duplicate check — triggers duplicate hit against source entity with matching details.',
    expectedResult: 'DUPLICATE',
    documents: ['Passport (THA)'],
    flags: [],
    type: 'individual',
    workflow: 'AUS-Basic2V-TwoPlus-Duplicate',
    individual: {
      name: { displayName: 'Driver Cover', givenName: 'Driver', familyName: 'Cover', language: 'ENG' },
      dateOfBirth: { year: '1985', month: '12', day: '15' },
      nationality: 'AUS', schemaVersion: 2,
      addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', streetNumber: '37', streetName: 'Koloona', streetType: 'Drive', locality: 'Tapitallee', subdivision: 'NSW', postalCode: '2540', country: 'AUS' }],
      documents: { IDENTITY: [{ type: 'PASSPORT', class: 'IDENTITY', primaryIdentifier: 'TH8598742', country: 'THA' }] },
      consents: CONSENTS,
    },
  },
  {
    id: 'james-testone-blocklist-phone',
    label: 'James A Testone (Blocklist Phone)',
    description: 'Block list — phone number +61412345678 triggers blocklist match.',
    expectedResult: 'BLOCKLIST',
    documents: ['Drivers Licence (VIC)'],
    flags: [],
    type: 'individual',
    individual: {
      name: { displayName: 'James A Testone', givenName: 'James', middleName: 'A', familyName: 'Testone', language: 'ENG' },
      dateOfBirth: { year: '1950', month: '01', day: '01' },
      nationality: 'AUS', schemaVersion: 2,
      addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', unitNumber: 'u 1', streetNumber: '35', streetName: 'Conn', streetType: 'Street', locality: 'Ferntree Gully', subdivision: 'VIC', postalCode: '3156', country: 'AUS' }],
      phoneNumbers: [{ country: 'AUS', number: '+61412345678', type: 'MOBILE' }],
      documents: { IDENTITY: [{ type: 'DRIVERS_LICENSE', class: 'IDENTITY', primaryIdentifier: '283229690', secondaryIdentifier: 'P5403241', country: 'AUS', subdivision: 'VIC' }] },
      consents: CONSENTS,
    },
  },
  {
    id: 'james-testone-blocklist-email',
    label: 'James A Testone (Blocklist Email)',
    description: 'Block list — email domain scam.com triggers blocklist match.',
    expectedResult: 'BLOCKLIST',
    documents: ['Drivers Licence (VIC)'],
    flags: [],
    type: 'individual',
    individual: {
      name: { displayName: 'James A Testone', givenName: 'James', middleName: 'A', familyName: 'Testone', language: 'ENG' },
      dateOfBirth: { year: '1950', month: '01', day: '01' },
      nationality: 'AUS', schemaVersion: 2,
      addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', unitNumber: 'u 1', streetNumber: '35', streetName: 'Conn', streetType: 'Street', locality: 'Ferntree Gully', subdivision: 'VIC', postalCode: '3156', country: 'AUS' }],
      emailAddresses: [{ email: 'test@scam.com', type: 'WORK' }],
      documents: { IDENTITY: [{ type: 'DRIVERS_LICENSE', class: 'IDENTITY', primaryIdentifier: '283229690', secondaryIdentifier: 'P5403241', country: 'AUS', subdivision: 'VIC' }] },
      consents: CONSENTS,
    },
  },
  {
    id: 'king-yorker',
    label: 'King Yorker',
    description: 'Risk check — Finnish passport with AU residence scores 51 (High Risk).',
    expectedResult: 'HIGH_RISK',
    documents: ['Passport (FIN)'],
    flags: [],
    type: 'individual',
    individual: {
      name: { displayName: 'King Yorker', givenName: 'King', familyName: 'Yorker' },
      dateOfBirth: { year: '1973', month: '02', day: '08' },
      nationality: 'AUS', schemaVersion: 2,
      addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', streetNumber: '24', streetName: 'Pimento', streetType: 'Circle', locality: 'Port Kennedy', subdivision: 'WA', postalCode: '6172', country: 'AUS' }],
      documents: { IDENTITY: [{ type: 'PASSPORT', class: 'IDENTITY', primaryIdentifier: 'FN9632853', country: 'FIN' }] },
      consents: CONSENTS,
    },
  },
  {
    id: 'emma-march',
    label: 'Emma March',
    description: 'AML-only — Refinitiv mock triggers PEP and Sanction hits.',
    expectedResult: 'PEP,SANCTION',
    documents: ['Passport (AUS)'],
    flags: ['PEP', 'Sanction'],
    type: 'individual',
    individual: {
      name: { displayName: 'Emma March', givenName: 'Emma', familyName: 'March' },
      dateOfBirth: { year: '1981', month: '10', day: '04' },
      nationality: 'AUS', schemaVersion: 2,
      addresses: [{ type: 'RESIDENTIAL', status: 'CURRENT', streetNumber: '63', streetName: 'Geelong', streetType: 'Street', locality: 'East Brisbane', subdivision: 'QLD', postalCode: '4169', country: 'AUS' }],
      documents: { IDENTITY: [{ type: 'PASSPORT', class: 'IDENTITY', primaryIdentifier: 'E4193261', country: 'AUS' }] },
      consents: CONSENTS,
    },
  },
  // ---- DVS (Document Verification Service) Entities ----
  {
    id: 'gavin-pearce-dvs',
    label: 'Gavin Pearce',
    description: 'DVS full match — all details match government records.',
    expectedResult: 'DVS MATCH',
    documents: ['Drivers Licence (VIC)'],
    flags: [],
    type: 'individual',
    workflow: 'au-dvsonly',
    individual: {
      name: { givenName: 'GAVIN', familyName: 'PEARCE' },
      dateOfBirth: { year: '1996', month: '06', day: '06' },
      addresses: [{ type: 'RESIDENTIAL', streetNumber: '121', streetName: 'King', streetType: 'Street', locality: 'Melbourne', subdivision: 'VIC', postalCode: '3000', country: 'AUS' }],
      documents: { IDENTITY: [{ type: 'DRIVERS_LICENSE', class: 'IDENTITY', primaryIdentifier: '111111111', secondaryIdentifier: 'P1234567', country: 'AUS', subdivision: 'VIC' }] },
      consents: CONSENTS,
    },
  },
  {
    id: 'david-littleproud-dvs',
    label: 'David Littleproud',
    description: 'DVS no match — DOB does not match government records.',
    expectedResult: 'DVS NO MATCH',
    documents: ['Drivers Licence (VIC)'],
    flags: [],
    type: 'individual',
    workflow: 'au-dvsonly',
    individual: {
      name: { givenName: 'DAVID', familyName: 'LITTLEPROUD' },
      dateOfBirth: { year: '1996', month: '06', day: '06' },
      addresses: [{ type: 'RESIDENTIAL', streetNumber: '121', streetName: 'King', streetType: 'Street', locality: 'Melbourne', subdivision: 'VIC', postalCode: '3000', country: 'AUS' }],
      documents: { IDENTITY: [{ type: 'DRIVERS_LICENSE', class: 'IDENTITY', primaryIdentifier: '111111111', secondaryIdentifier: 'P1234567', country: 'AUS', subdivision: 'VIC' }] },
      consents: CONSENTS,
    },
  },
  {
    id: 'lisa-chesters-dvs',
    label: 'Lisa Chesters',
    description: 'DVS no match — name does not match government records.',
    expectedResult: 'DVS NO MATCH',
    documents: ['Drivers Licence (VIC)'],
    flags: [],
    type: 'individual',
    workflow: 'au-dvsonly',
    individual: {
      name: { givenName: 'LISA', familyName: 'CHESTERS' },
      dateOfBirth: { year: '1996', month: '06', day: '06' },
      addresses: [{ type: 'RESIDENTIAL', streetNumber: '121', streetName: 'King', streetType: 'Street', locality: 'Melbourne', subdivision: 'VIC', postalCode: '3000', country: 'AUS' }],
      documents: { IDENTITY: [{ type: 'DRIVERS_LICENSE', class: 'IDENTITY', primaryIdentifier: '111111111', secondaryIdentifier: 'P1234567', country: 'AUS', subdivision: 'VIC' }] },
      consents: CONSENTS,
    },
  },
  // ---- Transaction Monitoring Entities ----
  {
    id: 'bruce-testnine-tm',
    label: 'Bruce F Testnine',
    description: 'Transaction monitoring — fraud email (bruce@highrisk.com) triggers fraud flag, then records LOGIN, two bank transfers ($110K + $12K), and an email change event.',
    expectedResult: 'FRAUD (TM)',
    documents: [],
    flags: ['Fraud'],
    type: 'individual',
    individual: {
      name: { givenName: 'BRUCE', middleName: 'F', familyName: 'TESTNINE' },
      dateOfBirth: { year: '1958', month: '01', day: '01' },
      emailAddresses: [{ email: 'bruce@highrisk.com', type: 'PERSONAL', isPreferred: true }],
      phoneNumbers: [{ number: '0400000009', type: 'MOBILE', country: 'AUS', isPreferred: true }],
      addresses: [{
        type: 'RESIDENTIAL',
        status: 'CURRENT',
        streetNumber: '81',
        streetName: 'ARGYLE',
        streetType: 'ROAD',
        locality: 'MARYBOROUGH',
        subdivision: 'VIC',
        postalCode: '3465',
        country: 'AUS',
      }],
      consents: [{ type: 'GENERAL' }, { type: 'DOCS' }],
    },
  },
  // ---- Business (KYB) Entities ----
  {
    id: 'fox-pty-ltd',
    label: 'FOX PTY. LTD.',
    description: 'Australian company — KYB profile lookup by ACN/ABN. Demonstrates business verification and ownership retrieval.',
    expectedResult: 'KYB PROFILE',
    documents: ['ACN: 052121347', 'ABN: 37052121347'],
    flags: [],
    type: 'organization',
    organization: {
      details: {
        registrationDetails: [
          { number: '37052121347', type: 'ABN', country: 'AUS' },
          { number: '052121347', type: 'ACN', country: 'AUS' },
        ],
      },
    },
  },
  {
    id: 'claro-disability',
    label: 'CLARO DISABILITY SERVICES PTY LTD',
    description: 'Australian company — KYB profile lookup by ACN. Demonstrates business verification and ownership retrieval.',
    expectedResult: 'KYB PROFILE',
    documents: ['ACN: 082554630'],
    flags: [],
    type: 'organization',
    organization: {
      details: {
        registrationDetails: [
          { number: '082554630', type: 'ACN', country: 'AUS' },
        ],
      },
    },
  },
  {
    id: 'bcp-pitt-street',
    label: 'BCP (PITT STREET) PTY LTD',
    description: 'Australian company — KYB profile lookup by ACN. Demonstrates business verification and ownership retrieval.',
    expectedResult: 'KYB PROFILE',
    documents: ['ACN: 650193865'],
    flags: [],
    type: 'organization',
    organization: {
      details: {
        registrationDetails: [
          { number: '650193865', type: 'ACN', country: 'AUS' },
        ],
      },
    },
  },
]
